import { useEffect, useMemo, useState } from "react";

const NAV_HEIGHT_TOP = 76;
const NAV_HEIGHT_SCROLLED = 60;

const links = [
  { label: "Tratamientos", href: "#features" },
  { label: "Especialista", href: "#nosotros" },
  { label: "Contacto", href: "#contact" },
];

function scrollToHash(href) {
  const id = href.replace("#", "");
  const el = document.getElementById(id);
  if (!el) return;
  el.scrollIntoView({ behavior: "smooth", block: "start" });
}

// Iconos
function IconMenu({ className = "" }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M4 7h16" />
      <path d="M4 12h16" />
      <path d="M4 17h16" />
    </svg>
  );
}

function IconClose({ className = "" }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M6 6l12 12" />
      <path d="M18 6l-12 12" />
    </svg>
  );
}

function WhatsAppIcon({ className = "" }) {
  return (
    <svg viewBox="0 0 32 32" fill="currentColor" className={className}>
      <path d="M16 3C9.37 3 4 8.37 4 15c0 2.64.86 5.08 2.31 7.06L4 29l7.13-2.27C13.06 27.43 14.5 28 16 28c6.63 0 12-5.37 12-12S22.63 3 16 3zm6.62 17.09c-.28.8-1.39 1.49-2.26 1.67-.59.12-1.36.17-4.39-.94-3.87-1.35-6.36-4.83-6.55-5.08-.18-.24-1.57-2.09-1.57-3.99 0-1.9.99-2.84 1.34-3.23.35-.39.77-.49 1.03-.49h.74c.24 0 .56-.09.87.66.31.74 1.05 2.59 1.14 2.78.09.19.15.42.03.67-.12.24-.18.39-.36.6-.18.21-.38.47-.54.63-.18.18-.37.37-.16.73.21.35.95 1.57 2.04 2.54 1.4 1.25 2.58 1.64 2.94 1.82.36.18.57.15.78-.09.21-.24.9-1.05 1.14-1.41.24-.36.48-.3.8-.18.33.12 2.07.98 2.43 1.16.36.18.6.27.69.42.09.15.09.87-.19 1.67z" />
    </svg>
  );
}

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 16);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Bloquear scroll cuando el menú móvil está abierto
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  // Cerrar con ESC
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  const style = useMemo(
    () => ({ height: scrolled ? NAV_HEIGHT_SCROLLED : NAV_HEIGHT_TOP }),
    [scrolled]
  );

  return (
    <>
      <header className="fixed inset-x-0 top-0 z-50 pointer-events-none">
        <div className="mx-auto max-w-6xl px-4 pt-3">
          {/* Glass pill */}
          <div
            style={style}
            className={[
              "pointer-events-auto",
              "flex items-center justify-between gap-4",
              "rounded-[28px]",
              "border border-white/30",
              "backdrop-blur-xl backdrop-saturate-150",
              "transition-all duration-300 ease-[cubic-bezier(.22,1,.36,1)]",
              scrolled
                ? "bg-white/85 shadow-[0_12px_30px_rgba(0,0,0,0.12)]"
                : "bg-white/65 shadow-[0_8px_20px_rgba(0,0,0,0.08)]",
            ].join(" ")}
          >
            {/* LEFT */}
            <button
  onClick={() => scrollToHash("#top")}
  className="flex items-center gap-3 pl-5 pr-3 h-full font-semibold tracking-tight text-gray-900"
  type="button"
>
  <img
    src="/img/Logobelskin1.png"
    alt="BELSKIN NURSE"
    className="h-9 w-9 rounded-xl object-contain"
  />
  <span className="whitespace-nowrap text-sm md:text-base">
    BELSKIN NURSE
  </span>
</button>

            {/* CENTER (desktop) */}
            <div className="hidden md:flex absolute left-1/2 -translate-x-1/2 items-center gap-1">
              {links.map((l) => (
                <button
                  key={l.href}
                  onClick={() => scrollToHash(l.href)}
                  className="
                    px-4 py-2 rounded-xl text-sm font-medium
                    text-gray-700
                    hover:text-[#366b63]
                    hover:bg-black/5
                    transition
                  "
                  type="button"
                >
                  {l.label}
                </button>
              ))}
            </div>

            {/* RIGHT */}
            <div className="flex items-center gap-2 pr-4">
              {/* WhatsApp (DESKTOP) -> se oculta si open */}
              {!open && (
                <a
                  href="https://wa.me/56920910798?text=Hola!%20Quiero%20m%C3%A1s%20informaci%C3%B3n."
                  target="_blank"
                  rel="noopener noreferrer"
                  className="
                    hidden md:flex
                    items-center justify-center
                    h-10 w-10 rounded-xl
                    bg-[#366b63]
                    text-white
                    shadow-[0_6px_16px_rgba(0,0,0,0.18)]
                    hover:brightness-110
                    transition
                  "
                  aria-label="WhatsApp"
                >
                  <WhatsAppIcon className="h-5 w-5" />
                </a>
              )}

              {/* WhatsApp (MOBILE) -> se oculta si open */}
              {!open && (
                <a
                  href="https://wa.me/56920910798?text=Hola!%20Quiero%20m%C3%A1s%20informaci%C3%B3n."
                  target="_blank"
                  rel="noopener noreferrer"
                  className="
                    md:hidden
                    flex items-center justify-center
                    h-10 w-10 rounded-xl
                    bg-[#366b63]
                    text-white
                    shadow-[0_6px_16px_rgba(0,0,0,0.18)]
                    hover:brightness-110
                    transition
                  "
                  aria-label="WhatsApp"
                >
                  <WhatsAppIcon className="h-5 w-5" />
                </a>
              )}

              {/* Toggle menú (mobile) */}
              <button
                onClick={() => setOpen((v) => !v)}
                className="
                  md:hidden
                  h-10 w-10 rounded-xl
                  bg-black/5
                  hover:bg-black/10
                  transition
                  flex items-center justify-center
                "
                aria-label={open ? "Cerrar menú" : "Abrir menú"}
                type="button"
              >
                {open ? (
                  <IconClose className="h-5 w-5 text-gray-800" />
                ) : (
                  <IconMenu className="h-5 w-5 text-gray-800" />
                )}
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* MOBILE MENU (overlay + panel glass) */}
      <div
        className={[
          "fixed inset-0 z-40 md:hidden",
          "transition-opacity duration-200",
          open ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none",
        ].join(" ")}
        aria-hidden={!open}
      >
        {/* overlay */}
        <div
          className="absolute inset-0 bg-black/40"
          onClick={() => setOpen(false)}
        />

        {/* panel */}
        <div className="absolute top-4 left-4 right-4">
          <div
            className="
              rounded-[28px]
              border border-white/35
              bg-white/80
              backdrop-blur-2xl backdrop-saturate-150
              shadow-[0_30px_90px_rgba(0,0,0,0.18)]
              overflow-hidden
            "
          >
            {/* header panel */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-black/5">
              <div className="flex items-center gap-3">
                <img
                  src="/img/Logobelskin1.png"
                  alt="Belskin Nurse"
                  className="h-9 w-9 rounded-xl object-contain"
                />
                <span className="font-semibold text-gray-900">Belskin Nurse</span>
              </div>

              <button
                onClick={() => setOpen(false)}
                className="
                  h-10 w-10 rounded-xl
                  bg-black/5 hover:bg-black/10
                  transition
                  flex items-center justify-center
                "
                aria-label="Cerrar"
                type="button"
              >
                <IconClose className="h-5 w-5 text-gray-800" />
              </button>
            </div>

            {/* links */}
            <nav className="p-4">
              <div className="flex flex-col gap-2">
                {links.map((l) => (
                  <button
                    key={l.href}
                    onClick={() => {
                      scrollToHash(l.href);
                      setOpen(false);
                    }}
                    className="
                      text-left px-4 py-3 rounded-2xl
                      text-gray-900 font-semibold
                      bg-black/0 hover:bg-black/5
                      transition
                    "
                    type="button"
                  >
                    {l.label}
                  </button>
                ))}
              </div>

              {/* WhatsApp botón grande (verde oscuro) */}
              <a
                href="https://wa.me/56920910798?text=Hola!%20Quiero%20m%C3%A1s%20informaci%C3%B3n."
                target="_blank"
                rel="noopener noreferrer"
                className="
                  mt-5 flex items-center justify-center gap-2
                  rounded-2xl bg-[#366b63]
                  text-white font-semibold
                  py-3
                  shadow-[0_14px_40px_rgba(54,107,99,0.22)]
                  hover:brightness-110
                  transition
                "
              >
                <WhatsAppIcon className="h-5 w-5" />
                WhatsApp
              </a>
            </nav>
          </div>
        </div>
      </div>
    </>
  );
}